import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  RotateCcw, ArrowLeft, Building2, Package,
  Save, Loader2, CheckCircle2, X, Info,
  Hash, Minus, Plus, ShoppingCart, UserCheck,
  ChevronDown, FileText, CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { returnService, customerService } from '../../../../services/erpApi';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useToast } from '../../../../hooks/useToast';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import { Toast } from '../../../../components';
import ERPSelectSearchModal, { ERPSelectModalItem, ERPSelectModalEmpty } from '../../../../components/ERP/Modal/ERPSelectSearchModal';
import './ReturnsCreatePage.css';

interface SelectedCustomer {
  id: number;
  name: string;
  cpf_cnpj?: string;
  email?: string;
  phone?: string;
}

interface SaleResult {
  id: number;
  number: string;
  access_key?: string;
  customer_id?: number | null;
  customer_name?: string | null;
  customer_cpf_cnpj?: string;
  issue_date: string;
  total_amount: number;
  status: string;
  seller_name?: string;
  url_pdf?: string;
}

interface SaleItem {
  id: number;
  product_id: number;
  product_name: string;
  product_sku?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount_amount?: number;
  effective_unit_price?: number;
}

interface ReturnLine {
  saleItemId: number;
  product_id: number;
  product_name: string;
  product_sku?: string;
  sold_quantity: number;
  return_quantity: number;
  unit_price: number;
  reason: string;
  selected: boolean;
}

const RETURN_REASONS = [
  'Defeito técnico',
  'Erro no faturamento',
  'Avaria no transporte',
  'Item em desacordo',
  'Arrependimento (B2B)'
];

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDocument(doc?: string) {
  if (!doc) return '';
  const digits = String(doc).replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
}

const ReturnsCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast, showToast, hideToast } = useToast();

  const canCreate = hasPermission(MODULES.STOCK, ACTIONS.STOCK_RETURNS_CREATE);

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [saleSearch, setSaleSearch] = useState('');
  const [saleResults, setSaleResults] = useState<SaleResult[]>([]);
  const [loadingSales, setLoadingSales] = useState(false);

  const [selectedSale, setSelectedSale] = useState<SaleResult | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<SelectedCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [returnLines, setReturnLines] = useState<ReturnLine[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [formData, setFormData] = useState({
    type: 'return' as 'exchange' | 'return',
    observation: ''
  });

  useEffect(() => {
    if (!saleModalOpen) return;

    const searchSales = async () => {
      if (saleSearch.length < 3) {
        setSaleResults([]);
        return;
      }

      setLoadingSales(true);
      try {
        const response = await returnService.searchSales(saleSearch);
        if (response.success) {
          setSaleResults(response.data || []);
        }
      } catch (error) {
        console.error('Erro ao buscar vendas:', error);
        setSaleResults([]);
      } finally {
        setLoadingSales(false);
      }
    };

    const timeoutId = setTimeout(searchSales, 400);
    return () => clearTimeout(timeoutId);
  }, [saleSearch, saleModalOpen]);

  useEffect(() => {
    if (!customerModalOpen) return;

    const searchCustomers = async () => {
      if (customerSearch.length < 3) {
        setCustomerResults([]);
        return;
      }

      setLoadingCustomers(true);
      try {
        const response = await customerService.getAll({
          status: 'active',
          search: customerSearch.trim(),
          limit: 50,
        });
        if (response.success) {
          setCustomerResults(response.data || []);
        } else {
          setCustomerResults([]);
        }
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        setCustomerResults([]);
      } finally {
        setLoadingCustomers(false);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 400);
    return () => clearTimeout(timeoutId);
  }, [customerSearch, customerModalOpen]);

  const loadSaleItems = useCallback(async (sale: SaleResult) => {
    setLoadingItems(true);
    setReturnLines([]);
    try {
      const response = await returnService.getInvoiceItems('sale', sale.id);
      if (response.success) {
        const items: SaleItem[] = response.data || [];
        setReturnLines(
          items.map((item) => {
            const effectiveUnitPrice = Number(
              item.effective_unit_price ?? item.unit_price
            );
            return {
              saleItemId: item.id,
              product_id: item.product_id,
              product_name: item.product_name,
              product_sku: item.product_sku,
              sold_quantity: Math.floor(Number(item.quantity)) || 1,
              return_quantity: Math.floor(Number(item.quantity)) || 1,
              unit_price: effectiveUnitPrice,
              reason: RETURN_REASONS[0],
              selected: false
            };
          })
        );
      }
    } catch (error) {
      console.error('Erro ao carregar itens da venda:', error);
      showToast('Erro ao carregar itens da venda', 'error');
    } finally {
      setLoadingItems(false);
    }
  }, [showToast]);

  const handleSelectSale = async (sale: SaleResult) => {
    setSelectedSale(sale);
    setSelectedCustomer(null);
    setSaleModalOpen(false);
    setSaleSearch('');
    setSaleResults([]);
    await loadSaleItems(sale);
  };

  const handleClearSale = () => {
    setSelectedSale(null);
    setSelectedCustomer(null);
    setReturnLines([]);
  };

  const handleSelectCustomer = (customer: SelectedCustomer) => {
    setSelectedCustomer({
      id: customer.id,
      name: customer.name,
      cpf_cnpj: customer.cpf_cnpj,
    });
    setCustomerModalOpen(false);
    setCustomerSearch('');
    setCustomerResults([]);
  };

  const saleHasCustomer = Boolean(selectedSale?.customer_id);
  const resolvedCustomerId = selectedSale?.customer_id || selectedCustomer?.id || null;
  const resolvedCustomerName = saleHasCustomer
    ? (selectedSale?.customer_name || '')
    : (selectedCustomer?.name || '');
  const resolvedCustomerDocument = saleHasCustomer
    ? selectedSale?.customer_cpf_cnpj
    : selectedCustomer?.cpf_cnpj;

  const toggleLine = (saleItemId: number, selected: boolean) => {
    setReturnLines((prev) =>
      prev.map((line) =>
        line.saleItemId === saleItemId ? { ...line, selected } : line
      )
    );
  };

  const toggleAll = (selected: boolean) => {
    setReturnLines((prev) => prev.map((line) => ({ ...line, selected })));
  };

  const updateLineQty = (saleItemId: number, delta: number) => {
    setReturnLines((prev) =>
      prev.map((line) => {
        if (line.saleItemId !== saleItemId) return line;
        const next = Math.min(
          line.sold_quantity,
          Math.max(1, line.return_quantity + delta)
        );
        return { ...line, return_quantity: next, selected: true };
      })
    );
  };

  const setLineQty = (saleItemId: number, value: string) => {
    setReturnLines((prev) =>
      prev.map((line) => {
        if (line.saleItemId !== saleItemId) return line;
        const parsed = parseInt(value, 10);
        const next = Number.isNaN(parsed)
          ? 1
          : Math.min(line.sold_quantity, Math.max(1, parsed));
        return { ...line, return_quantity: next };
      })
    );
  };

  const setLineReason = (saleItemId: number, reason: string) => {
    setReturnLines((prev) =>
      prev.map((line) =>
        line.saleItemId === saleItemId ? { ...line, reason } : line
      )
    );
  };

  const selectedLines = useMemo(
    () => returnLines.filter((line) => line.selected),
    [returnLines]
  );

  const totalCredit = useMemo(
    () =>
      selectedLines.reduce(
        (sum, line) => sum + line.unit_price * line.return_quantity,
        0
      ),
    [selectedLines]
  );

  const allSelected =
    returnLines.length > 0 && returnLines.every((line) => line.selected);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canCreate) {
      showToast('Você não tem permissão para criar devoluções', 'error');
      return;
    }

    if (!selectedSale) {
      showToast('Selecione a venda original', 'error');
      return;
    }

    if (selectedLines.length === 0) {
      showToast('Marque pelo menos um item para devolver', 'error');
      return;
    }

    if (!resolvedCustomerId) {
      showToast('Selecione o cliente da devolução', 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await returnService.create({
        type: formData.type,
        saleId: selectedSale.id,
        customerId: resolvedCustomerId,
        observation: formData.observation,
        totalAmount: totalCredit,
        status: 'pending',
        items: selectedLines.map((line) => ({
          product_id: line.product_id,
          quantity: line.return_quantity,
          unit_price: line.unit_price,
          reason: line.reason
        }))
      });

      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => navigate('/erp/estoque/trocas'), 1800);
      } else {
        showToast(response.message || 'Erro ao criar devolução', 'error');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Erro ao criar devolução:', error);
      showToast(
        err.response?.data?.message || 'Erro ao criar devolução. Tente novamente.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const saleModalContent =
    saleSearch.length < 3 ? (
      <ERPSelectModalEmpty
        icon={<Hash size={48} />}
        message="Digite ao menos 3 caracteres para buscar"
      />
    ) : loadingSales ? (
      <ERPSelectModalEmpty
        icon={<Loader2 size={32} className="returns-create-spinner" />}
        message="Buscando vendas..."
      />
    ) : saleResults.length === 0 ? (
      <ERPSelectModalEmpty
        icon={<ShoppingCart size={48} />}
        message="Nenhuma venda encontrada"
      />
    ) : (
      saleResults.map((sale) => (
        <React.Fragment key={sale.id}>
          <ERPSelectModalItem
            selected={selectedSale?.id === sale.id}
            onClick={() => handleSelectSale(sale)}
            avatar={<ShoppingCart size={20} />}
            name={`Venda #${sale.number}`}
            detail={`${sale.customer_name || 'Sem cliente'} • ${new Date(sale.issue_date).toLocaleDateString('pt-BR')} • ${formatCurrency(Number(sale.total_amount))}`}
            checkIcon={<CheckCircle size={20} className="erp-select-modal-item-check" />}
          />
        </React.Fragment>
      ))
    );

  const customerModalContent =
    customerSearch.length < 3 ? (
      <ERPSelectModalEmpty
        icon={<UserCheck size={48} />}
        message="Digite ao menos 3 caracteres para buscar"
      />
    ) : loadingCustomers ? (
      <ERPSelectModalEmpty
        icon={<Loader2 size={32} className="returns-create-spinner" />}
        message="Buscando clientes..."
      />
    ) : customerResults.length === 0 ? (
      <ERPSelectModalEmpty
        icon={<Building2 size={48} />}
        message="Nenhum cliente encontrado"
      />
    ) : (
      customerResults.map((customer) => (
        <React.Fragment key={customer.id}>
          <ERPSelectModalItem
            selected={selectedCustomer?.id === customer.id}
            onClick={() => handleSelectCustomer(customer)}
            avatar={<Building2 size={20} />}
            name={customer.name || 'Sem nome'}
            detail={[
              customer.cpf_cnpj ? formatDocument(customer.cpf_cnpj) : null,
              customer.email,
              customer.phone,
            ]
              .filter(Boolean)
              .join(' • ') || 'Sem documento'}
            checkIcon={<CheckCircle size={20} className="erp-select-modal-item-check" />}
          />
        </React.Fragment>
      ))
    );

  if (!canCreate) {
    return (
      <div className="returns-create-page">
        <div className="returns-create-access-denied">
          <X size={48} />
          <h2>Acesso Negado</h2>
          <p>Você não tem permissão para criar devoluções.</p>
        </div>
      </div>
    );
  }

  if (saveSuccess) {
    return (
      <div className="returns-create-page">
        <div className="returns-create-success">
          <div className="returns-create-success-content">
            <CheckCircle2 size={64} />
            <h2>Protocolo registrado</h2>
            <p>Redirecionando para a lista de devoluções...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="returns-create-page">
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      <div className="returns-create-header">
        <div className="returns-create-header-left">
          <button
            type="button"
            className="returns-create-btn-back"
            onClick={() => navigate('/erp/estoque/trocas')}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="returns-create-title">
              Nova <span className="returns-create-title-accent">Devolução</span>
            </h1>
            <p className="returns-create-subtitle">Vincule a venda original e marque os itens devolvidos</p>
          </div>
        </div>
        <div className="returns-create-header-right">
          <button
            type="button"
            className="returns-create-btn returns-create-btn--cancel"
            onClick={() => navigate('/erp/estoque/trocas')}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="returns-create-btn returns-create-btn--save"
            onClick={handleSubmit}
            disabled={saving || !selectedSale || selectedLines.length === 0 || !resolvedCustomerId}
          >
            {saving ? <Loader2 size={18} className="returns-create-spinner" /> : <Save size={18} />}
            Registrar
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="returns-create-grid">
        <div className="returns-create-col-left">
          <div className="returns-create-card">
            <div className="returns-create-card-bg" />
            <h3 className="returns-create-card-title">
              <ShoppingCart size={16} className="returns-create-card-icon" />
              Venda Original
            </h3>
            <div className="returns-create-card-content">
              <div className="returns-create-field">
                <label className="returns-create-label">Buscar venda</label>
                <div className="returns-create-input-wrapper">
                  <Hash size={18} className="returns-create-input-icon" />
                  <input
                    type="text"
                    readOnly
                    className="returns-create-input returns-create-input-clickable"
                    placeholder="Número, chave NF-e ou cliente..."
                    value={
                      selectedSale
                        ? `Venda #${selectedSale.number}`
                        : ''
                    }
                    onClick={() => setSaleModalOpen(true)}
                  />
                  <ChevronDown size={14} className="returns-create-input-chevron" />
                </div>
                <p className="returns-create-field-hint">
                  {selectedSale
                    ? 'Clique para trocar a venda vinculada'
                    : 'Obrigatório — devoluções sem venda original não são permitidas'}
                </p>
              </div>

              {selectedSale && (
                <div className="returns-create-sale-summary">
                  <div className="returns-create-sale-row">
                    <span>Data</span>
                    <strong>
                      {new Date(selectedSale.issue_date).toLocaleDateString('pt-BR')}
                    </strong>
                  </div>
                  <div className="returns-create-sale-row">
                    <span>Total da venda</span>
                    <strong>{formatCurrency(Number(selectedSale.total_amount))}</strong>
                  </div>
                  {selectedSale.seller_name && (
                    <div className="returns-create-sale-row">
                      <span>Vendedor</span>
                      <strong>{selectedSale.seller_name}</strong>
                    </div>
                  )}
                  {selectedSale.access_key && (
                    <div className="returns-create-sale-row returns-create-sale-row--full">
                      <span>Chave NF-e</span>
                      <strong className="returns-create-access-key">{selectedSale.access_key}</strong>
                    </div>
                  )}
                  <button
                    type="button"
                    className="returns-create-clear-sale"
                    onClick={handleClearSale}
                  >
                    Remover venda vinculada
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="returns-create-card">
            <div className="returns-create-card-bg returns-create-card-bg--blue" />
            <h3 className="returns-create-card-title">
              <UserCheck size={16} className="returns-create-card-icon" />
              Cliente
            </h3>
            <div className="returns-create-card-content">
              <div className="returns-create-field">
                <label className="returns-create-label">Instituição</label>
                <div className="returns-create-input-wrapper">
                  <Building2 size={18} className="returns-create-input-icon" />
                  <input
                    type="text"
                    readOnly
                    className={`returns-create-input${
                      selectedSale && !saleHasCustomer ? ' returns-create-input-clickable' : ''
                    }`}
                    placeholder={
                      !selectedSale
                        ? 'Selecione uma venda para preencher...'
                        : saleHasCustomer
                          ? 'Cliente vinculado à venda'
                          : 'Clique para selecionar o cliente...'
                    }
                    value={resolvedCustomerName}
                    onClick={() => {
                      if (selectedSale && !saleHasCustomer) {
                        setCustomerModalOpen(true);
                      }
                    }}
                  />
                  {selectedSale && !saleHasCustomer && (
                    <ChevronDown size={14} className="returns-create-input-chevron" />
                  )}
                </div>
                {resolvedCustomerDocument && (
                  <p className="returns-create-field-hint">
                    {formatDocument(resolvedCustomerDocument)}
                  </p>
                )}
                {selectedSale && !saleHasCustomer && !selectedCustomer && (
                  <p className="returns-create-field-hint returns-create-field-hint--warning">
                    Esta venda não possui cliente — selecione quem está devolvendo
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="returns-create-col-right">
          <div className="returns-create-products-card">
            <div className="returns-create-products-header">
              <div>
                <h3 className="returns-create-products-title">Itens da venda</h3>
                <p className="returns-create-products-subtitle">
                  Marque os produtos que serão devolvidos
                </p>
              </div>
              {returnLines.length > 0 && (
                <label className="returns-create-select-all">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                  Selecionar todos
                </label>
              )}
            </div>

            <div className="returns-create-items-container">
              {!selectedSale ? (
                <div className="returns-create-items-empty">
                  <FileText size={48} />
                  <p>Selecione uma venda para ver os itens disponíveis</p>
                </div>
              ) : loadingItems ? (
                <div className="returns-create-items-loading">
                  <Loader2 size={32} className="returns-create-spinner" />
                  <p>Carregando itens da venda...</p>
                </div>
              ) : returnLines.length > 0 ? (
                <table className="returns-create-items-table">
                  <thead>
                    <tr>
                      <th className="returns-create-th-check" />
                      <th>Produto</th>
                      <th className="returns-create-th-center">Qtd vendida</th>
                      <th className="returns-create-th-center">Qtd devolver</th>
                      <th className="returns-create-th-right">Valor unit.</th>
                      <th className="returns-create-th-right">Subtotal</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnLines.map((line) => (
                      <tr
                        key={line.saleItemId}
                        className={line.selected ? 'returns-create-item-row--selected' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            className="returns-create-checkbox"
                            checked={line.selected}
                            onChange={(e) => toggleLine(line.saleItemId, e.target.checked)}
                          />
                        </td>
                        <td>
                          <div className="returns-create-item-product">
                            <div className="returns-create-item-image">
                              <Package size={20} />
                            </div>
                            <div>
                              <p className="returns-create-item-name">{line.product_name}</p>
                              {line.product_sku && (
                                <p className="returns-create-item-sku">{line.product_sku}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="returns-create-td-center">{line.sold_quantity}</td>
                        <td>
                          <div className="returns-create-item-qty">
                            <button
                              type="button"
                              className="returns-create-item-qty-btn"
                              onClick={() => updateLineQty(line.saleItemId, -1)}
                              disabled={!line.selected && line.return_quantity <= 1}
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              max={line.sold_quantity}
                              className="returns-create-item-qty-value"
                              value={line.return_quantity}
                              disabled={!line.selected}
                              onChange={(e) => setLineQty(line.saleItemId, e.target.value)}
                              onFocus={() => {
                                if (!line.selected) toggleLine(line.saleItemId, true);
                              }}
                            />
                            <button
                              type="button"
                              className="returns-create-item-qty-btn"
                              onClick={() => updateLineQty(line.saleItemId, 1)}
                              disabled={line.return_quantity >= line.sold_quantity}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="returns-create-td-right">
                          {formatCurrency(line.unit_price)}
                        </td>
                        <td className="returns-create-td-right returns-create-item-total">
                          {formatCurrency(line.unit_price * line.return_quantity)}
                        </td>
                        <td>
                          <select
                            className="returns-create-reason-select"
                            value={line.reason}
                            disabled={!line.selected}
                            onChange={(e) => setLineReason(line.saleItemId, e.target.value)}
                          >
                            {RETURN_REASONS.map((reason) => (
                              <option key={reason} value={reason}>{reason}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="returns-create-items-empty">
                  <Package size={48} />
                  <p>Nenhum item encontrado nesta venda</p>
                </div>
              )}
            </div>
          </div>

          <div className="returns-create-sidebar">
            <div className="returns-create-field">
              <label className="returns-create-label">Tipo</label>
              <select
                className="returns-create-select"
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as 'exchange' | 'return'
                  }))
                }
              >
                <option value="return">Devolução</option>
                <option value="exchange">Troca</option>
              </select>
            </div>

            <div className="returns-create-summary">
              <div className="returns-create-summary-row">
                <span>Itens selecionados</span>
                <strong>{selectedLines.length}</strong>
              </div>
              <div className="returns-create-summary-row returns-create-summary-row--total">
                <span>Crédito estimado</span>
                <strong>{formatCurrency(totalCredit)}</strong>
              </div>
              <p className="returns-create-summary-hint">
                O crédito será gerado após aprovação da devolução
              </p>
            </div>

            <div className="returns-create-field">
              <label className="returns-create-label">Observações</label>
              <textarea
                className="returns-create-textarea"
                rows={3}
                placeholder="Informações adicionais sobre a devolução..."
                value={formData.observation}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, observation: e.target.value }))
                }
              />
            </div>

            <div className="returns-create-info-box">
              <Info size={18} />
              <p>
                Apenas itens da venda original podem ser devolvidos. A quantidade não pode
                exceder o que foi vendido.
              </p>
            </div>
          </div>
        </div>
      </form>

      <ERPSelectSearchModal
        isOpen={customerModalOpen}
        onClose={() => {
          setCustomerModalOpen(false);
          setCustomerSearch('');
          setCustomerResults([]);
        }}
        title="Buscar cliente"
        subtitle="Devolução de venda sem cliente vinculado"
        hint="Mínimo 3 caracteres — nome, CPF/CNPJ, e-mail ou telefone"
        icon={<UserCheck size={20} />}
        searchPlaceholder="Nome, CPF/CNPJ, e-mail ou telefone..."
        searchTerm={customerSearch}
        onSearchChange={setCustomerSearch}
        loading={loadingCustomers}
        footer={
          <p className="returns-create-modal-footer-hint">
            <Info size={12} /> Somente clientes ativos
          </p>
        }
        children={customerModalContent}
      />

      <ERPSelectSearchModal
        isOpen={saleModalOpen}
        onClose={() => {
          setSaleModalOpen(false);
          setSaleSearch('');
          setSaleResults([]);
        }}
        title="Buscar venda original"
        subtitle="Devoluções vinculadas à venda"
        hint="Mínimo 3 caracteres — número, chave NF-e ou cliente"
        icon={<RotateCcw size={20} />}
        searchPlaceholder="Número da venda, chave NF-e ou cliente..."
        searchTerm={saleSearch}
        onSearchChange={setSaleSearch}
        loading={loadingSales}
        footer={
          <p className="returns-create-modal-footer-hint">
            <Info size={12} /> Somente vendas confirmadas ou concluídas
          </p>
        }
        children={saleModalContent}
      />
    </div>
  );
};

export default ReturnsCreatePage;
